import React, { useEffect, useRef } from 'react';
import ChartUtils, { GraphType } from './chart-utils';
import * as echarts from 'echarts/core';
import BunnyDate from '../../../utils/date';
import { graphql } from 'gatsby';

export function ManualGraph({
  block,
  providers,
  entry,
}: {
  block: GraphBlock;
  providers: GraphProvider[];
  entry: EditorsEntry;
}) {
  let entries: ManualEntry[] = [];
  entry.dates.forEach((date, entryIndex) => {
    entry.cdn_providers.forEach(({ cdn_provider, values }) => {
      entries.push({
        cdnid: Number.parseInt(cdn_provider),
        continent: block.title,
        x: date,
        y: values[entryIndex] ?? 0,
      });
    });
  });

  const cdnItems = entries.map(({ cdnid }) => cdnid);

  return (
    <Graph
      cdnItems={cdnItems}
      providers={providers}
      entries={entries}
      lineType={block.type}
      isManual={true}
    />
  );
}

export function PerfopsGraph({
  block,
  providers,
  entry,
  data,
  continent,
}: {
  block: GraphBlock;
  providers: GraphProvider[];
  entry: PerfopsEntry;
  data: GraphData;
  continent: string;
}) {
  if (!data || !continent) {
    return;
  }

  let worldwideEntries: WorldwideEntry[] = [];
  let continentEntries: ContinentEntry[] = [];

  const entryType =
    continent === 'worldwide' ? 'worldwide_entries' : 'continents_entries';
  const timeScaleType =
    entry.time_scale === 'day' ? 'daily_entries' : 'monthly_entries';

  if (entryType === 'worldwide_entries') {
    worldwideEntries.push(...data['worldwide_entries'][timeScaleType]);
  } else {
    const entriesSubset = data[entryType][timeScaleType];
    const byContinent = (entry: ContinentEntry) =>
      `${continent}` === `${entry.continent}`;
    continentEntries.push(...entriesSubset.filter(byContinent));
  }

  return (
    <Graph
      cdnItems={entry.graph_items.map(cdnid => Number.parseInt(cdnid))}
      providers={providers}
      entries={continent === 'worldwide' ? worldwideEntries : continentEntries}
      lineType={block.type}
      isManual={false}
      timeScale={entry.time_scale}
    />
  );
}

export const GraphProxy = ({
  block,
  data,
  continent,
}: {
  block: GraphBlock;
  data: GraphData;
  continent: string;
}) => {
  if (!block.graph_entries || !block.graph_entries[0]) {
    return null;
  }

  const graphEntry = block.graph_entries[0];
  const type = graphEntry._template;
  const providers = data.providers;

  if (type === GraphType.Manual) {
    return (
      <ManualGraph
        block={block}
        providers={providers}
        entry={graphEntry as EditorsEntry}
      />
    );
  }
  if (type === GraphType.Perfops) {
    return (
      <PerfopsGraph
        block={block}
        providers={providers}
        entry={graphEntry as PerfopsEntry}
        data={data}
        continent={continent}
      />
    );
  }
  return null;
};

export function Graph({
  providers,
  entries,
  cdnItems,
  lineType,
  isManual,
  timeScale,
}: {
  cdnItems: number[];
  providers: GraphProvider[];
  entries: WorldwideEntry[] | ContinentEntry[] | ManualEntry[];
  lineType: string;
  isManual: boolean;
  timeScale?: 'day' | 'month' | string;
}) {
  const chartRef = useRef<HTMLDivElement | null>(null);

  // Resize chart on window resize
  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const chart = echarts.init(chartRef.current);

    function resizeHandler() {
      chart?.resize();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', resizeHandler);
    }

    let xAxisData: string[] = [];

    const graphSeries: any = cdnItems.map(cdnid => {
      // perfops returns cdnid as number, but graph_items is [string]
      const details = providers.find(provider => provider.cdnid === cdnid);
      const providerEntries = entries.filter(entry => entry.cdnid === cdnid);

      if (!details || !providerEntries) {
        return null;
      }

      const shouldCreateXAxis = xAxisData.length === 0;
      const ySeries: number[] = [];

      for (const { x, y } of providerEntries) {
        if (shouldCreateXAxis) {
          if (isManual) {
            xAxisData.push(`${x}`);
          } else {
            xAxisData.push(
              BunnyDate.getTimeLabel(Number.parseInt(x as string), timeScale!)
            );
          }
        }
        ySeries.push(y);
      }

      if (lineType === 'lines') {
        return ChartUtils.getSeriesLine(details.color, details.name, ySeries, {
          symbolSize: details.options?.symbol_size,
          lineWidth: details.options?.line_width,
          fontWeight: details.options?.font_weight,
        });
      } else {
        return ChartUtils.getSeriesGradient(
          details.color,
          details.name,
          ySeries
        );
      }
    });

    const { min, max, avg } = ChartUtils.getYBoundaries(graphSeries);
    let difference = max - min;
    difference = Math.ceil(difference / 20) * 20;
    // minimum is nearest 10x number
    const minBound = Math.floor(min / 10) * 10;
    // maximum is nearest 10x number to average
    const maxBound =
      max > difference
        ? Math.ceil((avg + minBound / 3) / 10) * 10
        : Math.ceil(max / 10) * 10;

    // chart = echarts.getInstanceByDom(chartRef.current);
    const option = {
      ...ChartUtils.BASE_OPTIONS,
      ...ChartUtils.CHART_OPTIONS,
      ...ChartUtils.SCALE_OPTIONS,
      xAxis: {
        ...ChartUtils.SCALE_OPTIONS.xAxis,
        data: xAxisData,
      },
      series: graphSeries,
    };

    if (chart) {
      chart.setOption({
        ...option,
        yAxis: {
          ...option.yAxis,
          splitNumber: 5,
          min: minBound,
          max: maxBound,
        },
      });
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', resizeHandler);
      }
      chart?.dispose();
    };
  }, [entries]);

  return (
    <div
      ref={chartRef}
      className='bunny-graph'
    />
  );
}

export const providersFragment = graphql`
  fragment ProvidersFragment on SidebarJson {
    providers {
      cdnid
      color
      name
      options {
        font_weight
        line_width
        symbol_size
      }
    }
  }
`;

export const continentsFragment = graphql`
  fragment ContinentsFragment on SidebarJson {
    continents {
      id
      iso
      name
    }
  }
`;

export const continentEntriesFragment = graphql`
  fragment ContinentEntriesFragment on SidebarJson {
    continents_entries {
      daily_entries {
        x
        y
        cdnid
        continent
      }
      monthly_entries {
        x
        y
        cdnid
        continent
      }
    }
  }
`;

export const worldwideEntriesFragment = graphql`
  fragment WorldwideEntriesFragment on SidebarJson {
    worldwide_entries {
      daily_entries {
        x
        y
        cdnid
      }
      monthly_entries {
        x
        y
        cdnid
      }
    }
  }
`;

export const entryFragment = graphql`
  fragment EntryFragment on Block {
    settings {
      css_class_name
      custom_css
      negative_margin
      padding_top
      padding_bottom
      margin_bottom
    }
    title
    type
    time_scale
    continents
    graph_items
  }
`;

export const graphFragment = graphql`
  fragment GraphFragment on Block {
    settings {
      css_class_name
      custom_css
      negative_margin
      padding_top
      padding_bottom
      margin_bottom
    }
    title
    type
    graph_entries {
      _template
      time_scale
      continents
      graph_items
      dates
      cdn_providers {
        cdn_provider
        values
      }
    }
  }
`;
